import React from 'react';
import { Box, Typography, Button, alpha, useTheme } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { useTranslation } from 'react-i18next';
import { popIn, celebrateBurst, TIMING, EASING } from './animations';

interface CompletionViewProps {
  /** Called when user dismisses the completion view */
  onDismiss: () => void;
}

/**
 * Success state shown when all activation steps are complete.
 *
 * Design:
 * - Professional celebration (not gamified)
 * - Animated success icon with burst effect
 * - Clear message and dismiss action
 * - Replaces the checklist in SidebarChecklist
 */
export function CompletionView({ onDismiss }: CompletionViewProps) {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Box
      sx={{
        textAlign: 'center',
        py: 3,
        px: 2,
        animation: `${popIn} ${TIMING.smooth} ${EASING.bounce}`,
        '@media (prefers-reduced-motion: reduce)': {
          animation: 'none',
        },
      }}
    >
      {/* Success icon with burst effect */}
      <Box
        sx={{
          position: 'relative',
          display: 'inline-flex',
          mb: 2,
        }}
      >
        {/* Background burst animation */}
        <Box
          sx={{
            position: 'absolute',
            inset: -8,
            borderRadius: '50%',
            bgcolor: alpha(theme.palette.success.main, 0.2),
            animation: `${celebrateBurst} 0.6s ${EASING.decelerate} forwards`,
            '@media (prefers-reduced-motion: reduce)': {
              animation: 'none',
              display: 'none',
            },
          }}
        />

        {/* Main success icon */}
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            bgcolor: 'success.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: `${popIn} ${TIMING.smooth} ${EASING.bounce}`,
            animationDelay: '100ms',
            opacity: 0,
            animationFillMode: 'forwards',
            boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.4)}`,
            '@media (prefers-reduced-motion: reduce)': {
              animation: 'none',
              opacity: 1,
            },
          }}
        >
          <CheckIcon sx={{ color: 'white', fontSize: 28 }} />
        </Box>
      </Box>

      {/* Success message */}
      <Typography
        variant="subtitle1"
        fontWeight={600}
        gutterBottom
        sx={{
          animation: `${popIn} ${TIMING.smooth} ${EASING.smooth}`,
          animationDelay: '150ms',
          opacity: 0,
          animationFillMode: 'forwards',
          '@media (prefers-reduced-motion: reduce)': {
            animation: 'none',
            opacity: 1,
          },
        }}
      >
        {t('components.activation.complete.title', "You're all set!")}
      </Typography>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          mb: 2,
          animation: `${popIn} ${TIMING.smooth} ${EASING.smooth}`,
          animationDelay: '200ms',
          opacity: 0,
          animationFillMode: 'forwards',
          '@media (prefers-reduced-motion: reduce)': {
            animation: 'none',
            opacity: 1,
          },
        }}
      >
        {t(
          'components.activation.complete.message',
          "You've completed the setup. AirPlx is ready to help you plan."
        )}
      </Typography>

      {/* Dismiss button */}
      <Button
        size="small"
        onClick={onDismiss}
        sx={{
          textTransform: 'none',
          color: 'text.secondary',
          fontWeight: 500,
          animation: `${popIn} ${TIMING.smooth} ${EASING.smooth}`,
          animationDelay: '250ms',
          opacity: 0,
          animationFillMode: 'forwards',
          '&:hover': {
            bgcolor: 'action.hover',
          },
          '@media (prefers-reduced-motion: reduce)': {
            animation: 'none',
            opacity: 1,
          },
        }}
      >
        {t('components.activation.complete.dismiss', 'Got it')}
      </Button>
    </Box>
  );
}
